import type { EndOfDayJob, EndOfDaySnapshot } from '../api/endOfDay'

function escCsv(v: string | number | null | undefined): string {
  const s = String(v ?? '')
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export function buildEodCsv(snapshot: EndOfDaySnapshot): string {
  const headers = [
    'Section',
    'Job',
    'Job Number',
    'Status',
    'Customer',
    'Property',
    'Crew',
    'Checklist %',
    'Photos',
    'Quoted Daily',
    'Actual Daily',
    'Exception',
  ]
  const rows: string[] = [headers.join(',')]

  const sections: Array<{ label: string; jobs: EndOfDayJob[] }> = [
    { label: 'Exceptions', jobs: snapshot.exceptions },
    { label: 'In Progress Today', jobs: snapshot.inProgressToday },
    { label: 'Other In Progress', jobs: snapshot.otherInProgress },
    { label: 'Not Started', jobs: snapshot.notStarted },
    { label: 'Completed Today', jobs: snapshot.completedToday },
  ]

  for (const section of sections) {
    for (const job of section.jobs) {
      const pct = job.checklistTotal
        ? Math.round((job.checklistDone / job.checklistTotal) * 100)
        : 0
      rows.push(
        [
          escCsv(section.label),
          escCsv(job.title),
          escCsv(job.job_number),
          escCsv(job.status),
          escCsv(job.customer?.company_name),
          escCsv(job.property?.address),
          escCsv(job.techs.join('; ')),
          pct,
          job.photos,
          job.daily_quoted_revenue.toFixed(2),
          job.daily_actual_revenue.toFixed(2),
          job.isException ? 'yes' : 'no',
        ].join(',')
      )
    }
  }

  return rows.join('\n')
}

export function downloadEodCsv(snapshot: EndOfDaySnapshot): void {
  const csv = buildEodCsv(snapshot)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `eod-summary_${snapshot.date}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function printEodSummary(snapshot: EndOfDaySnapshot, title = 'End of Day Summary'): void {
  const sectionHtml = (label: string, jobs: EndOfDayJob[]) => {
    if (!jobs.length) return ''
    const items = jobs
      .map((j) => `<li>${j.title} — ${j.status} — ${j.customer?.company_name || ''}</li>`)
      .join('')
    return `<h2>${label} (${jobs.length})</h2><ul>${items}</ul>`
  }

  const html = `
    <html>
      <head><title>${title}</title></head>
      <body>
        <h1>${title}</h1>
        <p>Date: ${snapshot.date} · Scheduled: ${snapshot.scheduledCount} · Completed: ${snapshot.completedToday.length} · Completion: ${snapshot.completionRate}%</p>
        <p>Exceptions: ${snapshot.exceptions.length} · Quoted: $${snapshot.totalQuotedRevenue.toFixed(2)} · Actual: $${snapshot.totalActualRevenue.toFixed(2)}</p>
        ${sectionHtml('Exceptions', snapshot.exceptions)}
        ${sectionHtml('In Progress Today', snapshot.inProgressToday)}
        ${sectionHtml('Other In Progress', snapshot.otherInProgress)}
        ${sectionHtml('Not Started', snapshot.notStarted)}
        ${sectionHtml('Completed Today', snapshot.completedToday)}
      </body>
    </html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
  w.print()
}
